import React from "react";
import { Checkbox, ListItemText, MenuItem, Select } from "@material-ui/core";
import { COLORS, FONT, RADIUS, cardStyle } from "./theme";

/**
 * Barra de filtros — UMA por tela, acima de tudo que ela recorta.
 *
 * Filtro dentro de card de gráfico é armadilha: dá para deixar dois blocos em
 * períodos diferentes lado a lado e ninguém percebe. Aqui todo o painel se
 * redesenha contra a mesma fatia.
 */

const inputStyle = {
  fontFamily: FONT,
  fontSize: 13,
  color: COLORS.ink,
  backgroundColor: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 9,
  padding: "8px 10px",
  outline: "none",
  boxSizing: "border-box",
};

const fieldLabelStyle = {
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: COLORS.inkMuted,
  display: "block",
  marginBottom: 5,
};

const PRESETS = [
  { key: "today", label: "Hoje" },
  { key: "week", label: "7 dias" },
  { key: "month", label: "Mês atual" },
  { key: "days30", label: "30 dias" },
  { key: "quarter", label: "90 dias" },
  { key: "year", label: "12 meses" },
];

const GRANULARITIES = [
  { key: "day", label: "Dia" },
  { key: "week", label: "Semana" },
  { key: "month", label: "Mês" },
];

/** Grupo de botões conectados: uma opção ativa, as outras em ghost. */
function Segmented({ options, value, onChange }) {
  return (
    <div
      style={{
        display: "inline-flex",
        backgroundColor: COLORS.subtle,
        borderRadius: 9,
        padding: 3,
        gap: 2,
      }}
    >
      {options.map((option) => {
        const active = option.key === value;
        return (
          <button
            key={option.key}
            type="button"
            onClick={() => onChange(option.key)}
            style={{
              fontFamily: FONT,
              fontSize: 12.5,
              fontWeight: active ? 600 : 500,
              color: active ? COLORS.ink : COLORS.inkSecondary,
              backgroundColor: active ? COLORS.surface : "transparent",
              boxShadow: active ? "0 1px 2px rgba(14, 32, 24, 0.10)" : "none",
              border: "none",
              borderRadius: 7,
              padding: "6px 12px",
              cursor: "pointer",
              transition: "background-color 140ms ease, color 140ms ease",
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** Atalhos de período — chips de seleção, não botões de ação. */
function PresetChips({ active, onSelect }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {PRESETS.map((preset) => {
        const selected = preset.key === active;
        return (
          <button
            key={preset.key}
            type="button"
            onClick={() => onSelect(preset.key)}
            style={{
              fontFamily: FONT,
              fontSize: 12.5,
              fontWeight: selected ? 600 : 500,
              color: selected ? COLORS.surface : COLORS.inkSecondary,
              backgroundColor: selected ? COLORS.brand : COLORS.surface,
              border: `1px solid ${selected ? COLORS.brand : COLORS.border}`,
              borderRadius: 999,
              padding: "6px 13px",
              cursor: "pointer",
              transition: "background-color 140ms ease, color 140ms ease",
            }}
          >
            {preset.label}
          </button>
        );
      })}
    </div>
  );
}

export default function FilterBar({
  fromDate,
  toDate,
  granularity,
  formIds,
  forms,
  activePreset,
  onChangeDate,
  onChangeGranularity,
  onChangeForms,
  onSelectPreset,
  onApply,
}) {
  return (
    <div style={{ ...cardStyle, borderRadius: RADIUS, padding: "14px 18px", marginBottom: 16 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 16 }}>
        <div>
          <label style={fieldLabelStyle} htmlFor="dash-from">
            Início
          </label>
          <input
            id="dash-from"
            type="date"
            value={fromDate}
            onChange={(event) => onChangeDate("fromDate", event.target.value)}
            style={{ ...inputStyle, width: 150 }}
          />
        </div>
        <div>
          <label style={fieldLabelStyle} htmlFor="dash-to">
            Fim
          </label>
          <input
            id="dash-to"
            type="date"
            value={toDate}
            onChange={(event) => onChangeDate("toDate", event.target.value)}
            style={{ ...inputStyle, width: 150 }}
          />
        </div>

        <div>
          <span style={fieldLabelStyle}>Agrupar por</span>
          <Segmented options={GRANULARITIES} value={granularity} onChange={onChangeGranularity} />
        </div>

        <div style={{ flex: "0 1 260px", minWidth: 200 }}>
          <span style={fieldLabelStyle}>Formulários</span>
          <Select
            multiple
            displayEmpty
            disableUnderline
            value={formIds}
            onChange={(event) => onChangeForms(event.target.value)}
            style={{ ...inputStyle, width: "100%", padding: "6px 10px" }}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <span style={{ color: COLORS.inkSecondary }}>Todos</span>;
              }
              if (selected.length === 1) {
                const form = forms.find((item) => item.id === selected[0]);
                return form ? form.name : "1 selecionado";
              }
              return `${selected.length} selecionados`;
            }}
            MenuProps={{ PaperProps: { style: { maxHeight: 340 } } }}
          >
            {forms.map((form) => (
              <MenuItem key={form.id} value={form.id} dense>
                <Checkbox size="small" checked={formIds.indexOf(form.id) !== -1} />
                <ListItemText
                  primary={form.name}
                  primaryTypographyProps={{ style: { fontFamily: FONT, fontSize: 13 } }}
                />
              </MenuItem>
            ))}
          </Select>
        </div>

        <button
          type="button"
          onClick={onApply}
          style={{
            fontFamily: FONT,
            fontSize: 13,
            fontWeight: 600,
            color: COLORS.surface,
            backgroundColor: COLORS.brand,
            border: "none",
            borderRadius: 9,
            padding: "10px 20px",
            cursor: "pointer",
          }}
        >
          Atualizar
        </button>
      </div>

      <div
        style={{
          marginTop: 14,
          paddingTop: 13,
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <PresetChips active={activePreset} onSelect={onSelectPreset} />
      </div>
    </div>
  );
}
